import React, { useState } from 'react';

import MuiTable from '@material-ui/core/Table';
import MuiTableBody from '@material-ui/core/TableBody';
import MuiTableCell from '@material-ui/core/TableCell';
import MuiTableHead from '@material-ui/core/TableHead';
import MuiTableRow from '@material-ui/core/TableRow';
import RowButtons from './row-buttons';
import MuiCircularProgress from '@material-ui/core/CircularProgress';

import pick from 'lodash/pick';
import keyBy from 'lodash/keyBy';

import SplitRow from './split-row';
import EditSplits from './edit-splits';
import { useQuery } from 'react-query';
import * as API from 'api';

const tableCellStyle = {
  textOverflow: 'ellipsis',
  overflow: 'hidden',
  whiteSpace: 'pre-wrap',
};

const Splits = API.Splits();
const Tracks = API.Tracks();
const editSplitsColumns = [
  {
    name: 'id',
    dataGetter: split => split.id,
    headerProps: { width: '8%' },
    cellProps: { style: { ...tableCellStyle } },
  },
  {
    name: 'Payee Name',
    dataGetter: split => split.payee?.payment_account?.group?.name || split.payee?.payment_account?.user?.name,
    headerProps: { width: '18%' },
    cellProps: { style: { ...tableCellStyle } },
  },
  {
    name: 'Payee Email',
    dataGetter: split => split.payee?.payment_account?.group?.email || split.payee?.payment_account?.user?.email,
    headerProps: { width: '21%' },
    cellProps: { style: { ...tableCellStyle } },
  },
  {
    name: 'Contributor Name',
    dataGetter: split => split.party?.full_name,
    headerProps: { width: '18%' },
    cellProps: { style: { ...tableCellStyle } },
  },
  {
    name: 'Roles',
    dataGetter: split => split.roles?.[0],
    headerProps: { width: '15%' },
    cellProps: { style: { ...tableCellStyle } },
  },
  {
    name: 'Wallet Address',
    dataGetter: split => split.payee?.payment_account?.wallet_address,
    headerProps: { width: '10%' },
    cellProps: { style: { ...tableCellStyle } },
  },
  {
    name: '%',
    dataGetter: split => split.percentage,
    headerProps: { width: '10%' },
    cellProps: { style: { ...tableCellStyle } },
  },
];

function SplitTrackBreakdownTable({ track, columns }) {
  const [isEditing, setIsExpanded] = useState(false);
  const [isInvalid, setIsInvalid] = useState(true);
  const [inProgress, setInProgress] = useState(false);
  const [splitFormValue, setSplitFormValue] = useState(null);
  const {
    data: splits,
    isLoading,
    refetch,
  } = useQuery(['Split', track?.id], () => Tracks.getSplits(track?.id), { enabled: !!track?.id });

  async function updateOrCreateSplit(formSplit) {
    const splitExists = !!formSplit.id;
    const splitUpdateObject = pick(formSplit, ['percentage', 'roles', 'user_group_payee_id', 'asset_party_id']);
    const { data: updatedSplit } = splitExists
      ? await Tracks.updateSplit(track.id, formSplit.id, splitUpdateObject)
      : await Tracks.addSplitWithContributor(track.id, splitUpdateObject);
    return updatedSplit;
  }

  async function updateSplitRelatedObjects(formSplits) {
    const splitIdMap = keyBy(splits, 'id');
    return await Promise.all(
      formSplits.map(async formSplit => {
        const matchingSplit = splitIdMap[formSplit.id];
        const updatedSplit = await updateOrCreateSplit(formSplit);
        const splitWithId = updatedSplit || matchingSplit;

        if ('payee_payment_account_wallet_address' in formSplit) {
          await Splits.updatePaymentAccount(splitWithId.stakeholder_track_split_id, {
            wallet_address: formSplit.payee_payment_account_wallet_address,
          });
        }
      })
    );
  }

  async function deleteSplits(formSplits) {
    const guiSplitSet = new Set(formSplits.map(formSplit => formSplit.id));
    const splitsToDelete = splits.filter(split => guiSplitSet.has(split.id) === false);
    return await Promise.all(splitsToDelete.map(async split => await Tracks.deleteSplit(track.id, split.id)));
  }

  async function saveSplits() {
    setInProgress(true);
    const formSplits = Object.values(splitFormValue);
    await deleteSplits(formSplits);
    await updateSplitRelatedObjects(formSplits);
    setInProgress(false);
    setIsExpanded(false);
    refetch();
  }

  const getColList = () => (isEditing ? editSplitsColumns : columns);

  if (!track || !track.isrc_code) return <span>ISRC NOT FOUND</span>;
  if (isLoading) return <MuiCircularProgress />;

  return (
    <MuiTable size="small" aria-label="purchases" style={{ tableLayout: isEditing ? 'fixed' : 'auto' }}>
      <MuiTableHead>
        <MuiTableRow>
          {getColList().map(col => (
            <MuiTableCell {...col.headerProps} key={`trackSplitColumn-${col.name}`}>
              {col.name}
            </MuiTableCell>
          ))}

          <MuiTableCell align="right" style={{ width: '10%' }}>
            <RowButtons
              editing={isEditing}
              onEditChange={setIsExpanded}
              onSave={saveSplits}
              inProgress={inProgress}
              invalid={isInvalid}
            />
          </MuiTableCell>
        </MuiTableRow>
      </MuiTableHead>
      <MuiTableBody>
        {isEditing ? (
          <EditSplits
            splits={splits}
            user_group_id={track.user_group_id}
            setIsInvalid={setIsInvalid}
            setSplitFormValue={setSplitFormValue}
            colDefs={editSplitsColumns}
          />
        ) : (
          splits.map(split => <SplitRow key={split.id} split={split} columns={columns} />)
        )}
      </MuiTableBody>
    </MuiTable>
  );
}

export default SplitTrackBreakdownTable;
